import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Eye, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Settings → Document Templates (10/07, Josh): edit the wording of the Payoff,
// Zero Balance and Payment History letters, and each funding company's
// letterhead / contact / wire details. Figures are never typed here — they
// come from the deal at generation time. "Preview" renders a sample letter
// with the SAVED values.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOC_ORDER = ['payoff', 'zbl', 'payment_history'];
const PROFILE_FIELDS = [
  ['name', 'Company name (letterhead)'], ['address', 'Mailing address (letterhead)'],
  ['phone', 'Letterhead phone'], ['email', 'Letterhead email'], ['signature_name', 'Signature line (contracts)'],
  ['sms_phone', 'Collections text call-back phone'],
  ['office_phone', 'Main / call-in phone (account-closed R02 text)'],
  ['contracts_email', 'Contracts email (funding-confirmation letterhead)'],
  // 10/28 (Josh: "need for dib too"): {payment_methods} in the bounce texts / notices — one method per line
  ['payment_methods', 'Payment methods offered in bounce texts / notices (one per line)'],
];
const PAYOFF_FIELDS = [
  ['legal_name', 'Legal name as signed'], ['signature', 'Signature line (letters)'],
  ['contact_phone', 'Contact phone (letters)'], ['contact_email', 'Contact email (letters)'],
];
const WIRE_FIELDS = [
  ['beneficiary', 'Beneficiary name'], ['bank_name', 'Bank'], ['account_number', 'Account number'],
  ['routing_number', 'Routing number'],
];

const DocumentTemplatesSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [tpl, setTpl] = useState(null);        // {templates, defaults, labels, placeholders}
  const [edits, setEdits] = useState({});      // {doc: {field: text}}
  const [profiles, setProfiles] = useState(null);
  const [profEdits, setProfEdits] = useState({}); // {KEY: {field, payoff:{..., wire:{...}}}}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/settings/document-templates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/entity-profiles`, { headers: getAuthHeaders() }),
      ]);
      setTpl(t.data);
      setEdits(t.data.templates);
      setProfiles(p.data);
      setProfEdits(p.data.profiles);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load document templates');
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveTemplates = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/settings/document-templates`, { templates: edits }, { headers: getAuthHeaders() });
      setEdits(res.data.templates);
      setTpl((t) => ({ ...t, templates: res.data.templates, overrides: res.data.overrides }));
      toast.success('Document wording saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const resetTemplates = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/settings/document-templates/reset`, {}, { headers: getAuthHeaders() });
      await load();
      toast.success('Wording reset to defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  const saveProfile = async (key) => {
    setBusy(true);
    try {
      const p = profEdits[key] || {};
      const body = {
        profile: {
          ...Object.fromEntries(PROFILE_FIELDS.map(([f]) => [f, p[f] ?? ''])),
          payoff: {
            ...Object.fromEntries(PAYOFF_FIELDS.map(([f]) => [f, p.payoff?.[f] ?? ''])),
            wire: {
              ...Object.fromEntries(WIRE_FIELDS.map(([f]) => [f, p.payoff?.wire?.[f] ?? ''])),
              bank_address: p.payoff?.wire?.bank_address ?? [],
            },
          },
        },
      };
      const res = await axios.put(`${API}/settings/entity-profiles/${key}`, body, { headers: getAuthHeaders() });
      setProfEdits((e) => ({ ...e, [key]: res.data.profile }));
      setProfiles((pr) => ({ ...pr, complete: { ...pr.complete, [key]: res.data.complete } }));
      toast.success(`${res.data.profile.name} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const resetProfile = async (key) => {
    setBusy(true);
    try {
      await axios.post(`${API}/settings/entity-profiles/${key}/reset`, {}, { headers: getAuthHeaders() });
      await load();
      toast.success('Company details reset to defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  const preview = async (docType, entity) => {
    try {
      const res = await axios.get(`${API}/settings/document-templates/preview`, {
        headers: getAuthHeaders(), params: { doc_type: docType, entity }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const setField = (doc, field, value) => setEdits((e) => ({ ...e, [doc]: { ...(e[doc] || {}), [field]: value } }));
  const setProf = (key, path, value) => setProfEdits((e) => {
    const cur = JSON.parse(JSON.stringify(e[key] || {}));
    let node = cur;
    for (let i = 0; i < path.length - 1; i += 1) { node[path[i]] = node[path[i]] || {}; node = node[path[i]]; }
    node[path[path.length - 1]] = value;
    return { ...e, [key]: cur };
  });

  if (!tpl || !profiles) return <p className="text-sm text-gray-500">Loading document templates…</p>;

  const isOverridden = (doc, field) => Boolean(tpl.overrides?.[doc]?.[field]);

  return (
    <div className="space-y-6" data-testid="document-templates-settings">
      {/* ── Funding companies ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Funding companies</CardTitle>
          <CardDescription>
            Letters are branded for the company that funded the deal (the deal's funder contact: Power Funding Solutions or DIB Capital).
            Deals with no funder on file use Power Funding Solutions. Contracts still require a complete profile.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.keys(profiles.profiles).map((key) => {
            const p = profEdits[key] || {};
            return (
              <div key={key} className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid={`entity-${key}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{p.name || key} <span className="text-xs text-gray-400">({key})</span></h3>
                  {profiles.complete?.[key]
                    ? <Badge variant="secondary">complete</Badge>
                    : <Badge variant="outline" className="text-amber-700 border-amber-300">address missing — letters only</Badge>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {PROFILE_FIELDS.map(([f, label]) => (
                    <div key={f} className={f === 'payment_methods' ? 'md:col-span-2' : ''}>
                      <Label className="text-xs">{label}</Label>
                      {f === 'payment_methods'
                        ? <Textarea rows={6} value={p[f] ?? ''} onChange={(e) => setProf(key, [f], e.target.value)} data-testid={`profile-payment-methods-${key}`} />
                        : <Input value={p[f] ?? ''} onChange={(e) => setProf(key, [f], e.target.value)} />}
                    </div>
                  ))}
                  {PAYOFF_FIELDS.map(([f, label]) => (
                    <div key={f}>
                      <Label className="text-xs">{label}</Label>
                      <Input value={p.payoff?.[f] ?? ''} onChange={(e) => setProf(key, ['payoff', f], e.target.value)} />
                    </div>
                  ))}
                </div>
                <p className="text-xs uppercase tracking-wide text-gray-500 pt-1">Wire instructions (payoff letter)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {WIRE_FIELDS.map(([f, label]) => (
                    <div key={f}>
                      <Label className="text-xs">{label}</Label>
                      <Input value={p.payoff?.wire?.[f] ?? ''} onChange={(e) => setProf(key, ['payoff', 'wire', f], e.target.value)} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <Label className="text-xs">Bank address (one line per row)</Label>
                    <Textarea rows={2} value={(p.payoff?.wire?.bank_address || []).join('\n')}
                      onChange={(e) => setProf(key, ['payoff', 'wire', 'bank_address'], e.target.value.split('\n'))} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={() => saveProfile(key)} disabled={busy}><Save className="h-4 w-4 mr-1" />Save</Button>
                  <Button size="sm" variant="outline" onClick={() => resetProfile(key)} disabled={busy}><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>
                  {DOC_ORDER.map((d) => (
                    <Button key={d} size="sm" variant="ghost" onClick={() => preview(d, key)}>
                      <Eye className="h-4 w-4 mr-1" />{tpl.labels.docs[d]}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* ── Wording ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Letter wording</CardTitle>
          <CardDescription>
            Edit the text of each letter. Use the placeholders below; amounts, dates and wire details are filled in from the deal and the funding company at generation time.
            A blank field uses the default wording.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Placeholders</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-xs text-gray-700">
              {Object.entries(tpl.placeholders).map(([k, v]) => (
                <div key={k}><code className="font-mono text-[#3b82f6]">{`{${k}}`}</code> — {v}</div>
              ))}
            </div>
          </div>

          {DOC_ORDER.map((doc) => (
            <div key={doc} className="space-y-3" data-testid={`template-${doc}`}>
              <h3 className="font-semibold text-gray-900">{tpl.labels.docs[doc]}</h3>
              {Object.keys(tpl.defaults[doc]).map((field) => {
                const multiline = (tpl.defaults[doc][field] || '').length > 60;
                const value = edits[doc]?.[field] ?? '';
                return (
                  <div key={field}>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">{tpl.labels.fields[field] || field}</Label>
                      {isOverridden(doc, field) && <Badge variant="secondary" className="text-[10px]">edited</Badge>}
                    </div>
                    {multiline
                      ? <Textarea rows={3} value={value} onChange={(e) => setField(doc, field, e.target.value)} />
                      : <Input value={value} onChange={(e) => setField(doc, field, e.target.value)} />}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="flex gap-2">
            <Button onClick={saveTemplates} disabled={busy}><Save className="h-4 w-4 mr-1" />Save wording</Button>
            <Button variant="outline" onClick={resetTemplates} disabled={busy}><RotateCcw className="h-4 w-4 mr-1" />Reset all to defaults</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentTemplatesSettings;
